import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { checkTokenExpiration } from '../utils/clearAuth';

const AuthContext = createContext();

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if partner is logged in on app start
    const token = localStorage.getItem('authToken');
    const partnerData = localStorage.getItem('partnerData');
    
    // Check if token is expired
    if (checkTokenExpiration()) {
      // Clear expired data but don't redirect during initial load
      localStorage.removeItem('authToken');
      localStorage.removeItem('partnerData');
      setPartner(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }
    
    if (token && partnerData) {
      try {
        setPartner(JSON.parse(partnerData));
        setIsAuthenticated(true);
        
        // Verify token is still valid
        verifyToken(token);
      } catch (error) {
        console.error('Error parsing partner data:', error);
        logout();
      }
    }
    setLoading(false);
  }, []);

  const verifyToken = async (token) => {
    try {
      const data = await authAPI.getProfile();

      if (data.success) {
        const partner = data.partner;
        
        if (!partner) {
          throw new Error('Invalid response format from server');
        }
        
        setPartner(partner);
        setIsAuthenticated(true);
      } else {
        throw new Error('Token verification failed');
      }
    } catch (error) {
      console.error('Token verification error:', error);
      // Don't call logout() here as it might cause infinite loops
      // Just clear the stored data
      localStorage.removeItem('authToken');
      localStorage.removeItem('partnerData');
      setPartner(null);
      setIsAuthenticated(false);
    }
  };

  const login = async (email, password) => {
    try {
      const data = await authAPI.login(email, password);

      if (data.success) {
        const token = data.token;
        const partner = data.partner;
        
        if (!token || !partner) {
          return { success: false, error: 'Invalid response format from server' };
        }

        // Store token and partner data
        localStorage.setItem('authToken', token);
        localStorage.setItem('partnerData', JSON.stringify(partner));
        
        setPartner(partner);
        setIsAuthenticated(true);
        
        return { success: true, partner: partner };
      } else {
        return { success: false, error: data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.message || 'Network error. Please try again.' 
      };
    }
  };

  const register = async (partnerData) => {
    try {
      const data = await authAPI.register(partnerData);

      if (data.success) {
        return { success: true, partner: data.partner };
      } else {
        return { success: false, error: data.message || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.message || 'Network error. Please try again.' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('partnerData');
    setPartner(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (profileData) => {
    try {
      const data = await authAPI.updateProfile(profileData);

      if (data.success) {
        const partner = data.partner;
        
        if (!partner) {
          return { success: false, error: 'Invalid response format from server' };
        }
        
        setPartner(partner);
        localStorage.setItem('partnerData', JSON.stringify(partner));
        return { success: true, partner: partner };
      } else {
        return { success: false, error: data.message || 'Profile update failed' };
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return { 
        success: false, 
        error: error.message || 'Network error. Please try again.' 
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const data = await authAPI.changePassword(currentPassword, newPassword);

      if (data.success) {
        return { success: true };
      } else {
        return { success: false, error: data.message || 'Password change failed' };
      }
    } catch (error) {
      console.error('Password change error:', error);
      return { 
        success: false, 
        error: error.message || 'Network error. Please try again.' 
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const data = await authAPI.forgotPassword(email);

      if (data.success) {
        return { success: true, message: data.message || 'Password reset email sent successfully' };
      } else {
        return { success: false, error: data.message || 'Failed to send password reset email' };
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      return { 
        success: false, 
        error: error.message || 'Network error. Please try again.' 
      };
    }
  };

  const value = {
    isAuthenticated,
    partner,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    forgotPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 